import { useUiStore } from '@/shared/hooks/use-shallow-store';
import type { DialogKind, DialogPropsMap } from './types';

/**
 * Convenience hook returning the dialog actions. Components that only
 * need to open a dialog can use `useDialog().open('rules', {})` without
 * needing to subscribe to the dialog stack itself.
 */
export const useDialog = (): {
  open: <K extends DialogKind>(kind: K, props: DialogPropsMap[K]) => string;
  close: (id?: string) => void;
  closeKind: (kind: DialogKind) => void;
  closeAll: () => void;
  update: <K extends DialogKind>(id: string, props: Partial<DialogPropsMap[K]>) => void;
} => {
  const open = useUiStore((s) => s.openDialog);
  const close = useUiStore((s) => s.closeDialog);
  const closeKind = useUiStore((s) => s.closeDialogByKind);
  const closeAll = useUiStore((s) => s.closeAllDialogs);
  const update = useUiStore((s) => s.updateDialog);
  return { open, close, closeKind, closeAll, update };
};

/** Read whether a dialog of the given kind is currently mounted. */
export const useIsDialogOpen = (kind: DialogKind): boolean =>
  useUiStore((s) => s.dialogs.some((d) => d.kind === kind));
